import React from 'react';
import { Box } from '@mui/material';

// TEMP (branding hidden for demo build): hides the logo app-wide
// (Landing + Dashboard). Revert: set to false (or run restore_branding.sh).
// See BRANDING_NEUTRALIZATION.md
const BRANDING_HIDDEN = true;

function AppLogo({ size = 72 }) {
  if (BRANDING_HIDDEN) return null;
  return (
    <Box sx={{
      width: size, height: size, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <svg viewBox="0 0 64 74" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="sideShieldFill" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#2a2545" />
            <stop offset="100%" stopColor="#1e1a32" />
          </linearGradient>
          <clipPath id="shieldClip">
            <path d="M32 2 L60 12 L60 38 C60 56 48 66 32 71 C16 66 4 56 4 38 L4 12 Z" />
          </clipPath>
          <filter id="sideGlow">
            <feGaussianBlur stdDeviation="0.8" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <filter id="sidePulseGlow">
            <feGaussianBlur stdDeviation="1.5" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {/* Shield outline */}
        <path d="M32 2 L60 12 L60 38 C60 56 48 66 32 71 C16 66 4 56 4 38 L4 12 Z"
              fill="url(#sideShieldFill)" stroke="#a78bfa" strokeWidth="1.2" strokeOpacity="0.45" />
        <path d="M32 5 L57 14 L57 38 C57 53 46 63 32 68 C18 63 7 53 7 38 L7 14 Z"
              fill="none" stroke="#a78bfa" strokeWidth="0.5" strokeOpacity="0.2" />

        {/* Network inside — nodes spread ~10% from center */}
        <g clipPath="url(#shieldClip)">
          {/* Edges */}
          <line x1="32" y1="25" x2="43" y2="17" stroke="#9090a0" strokeWidth="2" strokeLinecap="round" />
          <line x1="32" y1="25" x2="19" y2="34" stroke="#9090a0" strokeWidth="2" strokeLinecap="round" />
          <line x1="32" y1="25" x2="45" y2="34" stroke="#9090a0" strokeWidth="2" strokeLinecap="round" />
          <line x1="19" y1="34" x2="32" y2="43" stroke="#9090a0" strokeWidth="2" strokeLinecap="round" />

          <line x1="32" y1="43" x2="42" y2="53" stroke="#9090a0" strokeWidth="2" strokeLinecap="round" />

          {/* Data flow pulses */}
          <circle r="1.8" fill="#e0d4ff" filter="url(#sidePulseGlow)">
            <animateMotion dur="1.1s" repeatCount="indefinite" path="M32,25 L43,17" />
            <animate attributeName="opacity" values="0;1;1;0" dur="1.1s" repeatCount="indefinite" />
          </circle>
          <circle r="1.8" fill="#e0d4ff" filter="url(#sidePulseGlow)">
            <animateMotion dur="1.4s" repeatCount="indefinite" path="M19,34 L32,43" />
            <animate attributeName="opacity" values="0;1;1;0" dur="1.4s" repeatCount="indefinite" />
          </circle>
          <circle r="1.8" fill="#e0d4ff" filter="url(#sidePulseGlow)">
            <animateMotion dur="1.0s" repeatCount="indefinite" path="M32,43 L42,53" />
            <animate attributeName="opacity" values="0;1;1;0" dur="1.0s" repeatCount="indefinite" />
          </circle>
          <circle r="1.8" fill="#e0d4ff" filter="url(#sidePulseGlow)">
            <animateMotion dur="1.2s" repeatCount="indefinite" path="M32,25 L19,34" begin="0.3s" />
            <animate attributeName="opacity" values="0;1;1;0" dur="1.2s" repeatCount="indefinite" begin="0.3s" />
          </circle>

          <circle r="1.8" fill="#e0d4ff" filter="url(#sidePulseGlow)">
            <animateMotion dur="1.2s" repeatCount="indefinite" path="M32,25 L45,34" begin="0.5s" />
            <animate attributeName="opacity" values="0;1;1;0" dur="1.2s" repeatCount="indefinite" begin="0.5s" />
          </circle>

          {/* Red/coral nodes */}
          <circle cx="32" cy="25" r="5" fill="#EF6B5A" filter="url(#sideGlow)">
            <animate attributeName="r" values="5;5.8;5" dur="2.5s" repeatCount="indefinite" />
          </circle>
          <circle cx="19" cy="34" r="4.5" fill="#EF6B5A" filter="url(#sideGlow)">
            <animate attributeName="r" values="4.5;5.2;4.5" dur="3s" repeatCount="indefinite" />
          </circle>
          <circle cx="45" cy="34" r="4.5" fill="#EF6B5A" filter="url(#sideGlow)">
            <animate attributeName="r" values="4.5;5.2;4.5" dur="2.6s" repeatCount="indefinite" />
          </circle>

          {/* Blue nodes */}
          <circle cx="43" cy="17" r="5" fill="#3B82F6" filter="url(#sideGlow)">
            <animate attributeName="r" values="5;5.8;5" dur="2.2s" repeatCount="indefinite" />
          </circle>
          <circle cx="32" cy="43" r="5.5" fill="#3B82F6" filter="url(#sideGlow)">
            <animate attributeName="r" values="5.5;6.2;5.5" dur="2.8s" repeatCount="indefinite" />
          </circle>
          <circle cx="42" cy="53" r="4.5" fill="#3B82F6" filter="url(#sideGlow)">
            <animate attributeName="r" values="4.5;5.2;4.5" dur="2.4s" repeatCount="indefinite" />
          </circle>
        </g>
      </svg>
    </Box>
  );
}

export default AppLogo;
